import { useState, useEffect, useContext, createContext, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { applicationLogout } from '../services/LoginServices';

// Security Context
const SecurityContext = createContext();

// Security Provider Component
export const SecurityProvider = ({ children }) => {
	const loginRedux = useSelector((state) => state.loginRedux);
	const isUserLoggedIn = loginRedux?.isLogged || false;
	
	const [isLocked, setIsLocked] = useState(false);
	const [lockType, setLockType] = useState('pattern'); // 'pattern' or 'passcode'
	const [lockPattern, setLockPattern] = useState([]);
	const [lockPasscode, setLockPasscode] = useState('');
	const [inactivityTimeout, setInactivityTimeout] = useState(5); // minutes
	const [isEnabled, setIsEnabled] = useState(false);
	const [lastActivity, setLastActivity] = useState(Date.now());
	const [timeRemaining, setTimeRemaining] = useState(null); // seconds until auto-lock

	const inactivityTimerRef = useRef(null);
	const countdownTimerRef = useRef(null);
	const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

	// Load settings from localStorage on initial render
	useEffect(() => {
		// Only load settings if user is logged in
		if (!isUserLoggedIn) {
			console.log('User not logged in, clearing security settings');
			setLockPattern([]);
			setLockPasscode('');
			setIsEnabled(false);
			setIsLocked(false);
			setTimeRemaining(null);
			localStorage.removeItem('fitT-app-locked');
			localStorage.removeItem('fitT-security-settings');
			return;
		}

		const savedSettings = localStorage.getItem('fitT-security-settings');
		const savedLockState = localStorage.getItem('fitT-app-locked');
		
		if (savedSettings) {
			try {
				const settings = JSON.parse(savedSettings);
				setLockType(settings.lockType || 'pattern');
				setLockPattern(settings.lockPattern || []);
				setLockPasscode(settings.lockPasscode || '');
				setInactivityTimeout(settings.inactivityTimeout || 5);
				setIsEnabled(settings.isEnabled || false);
			} catch (error) {
				console.error('Error loading security settings:', error);
			}
		}

		// Restore lock state if app was locked before reload
		if (savedLockState === 'true') {
			setIsLocked(true);
		}
	}, [isUserLoggedIn]);

	// Save lock state to localStorage whenever it changes
	useEffect(() => {
		if (isLocked) {
			localStorage.setItem('fitT-app-locked', 'true');
		} else {
			localStorage.removeItem('fitT-app-locked');
		}
	}, [isLocked]);

	// Save settings to localStorage whenever they change (only if user is logged in)
	useEffect(() => {
		if (!isUserLoggedIn) return;
		
		const settings = {
			lockType,
			lockPattern,
			lockPasscode,
			inactivityTimeout,
			isEnabled
		};
		localStorage.setItem('fitT-security-settings', JSON.stringify(settings));
	}, [lockType, lockPattern, lockPasscode, inactivityTimeout, isEnabled, isUserLoggedIn]);

	// Reset inactivity timer and start countdown
	const resetInactivityTimer = useCallback(() => {
		setLastActivity(Date.now());
		
		if (inactivityTimerRef.current) {
			clearTimeout(inactivityTimerRef.current);
		}

		if (countdownTimerRef.current) {
			clearInterval(countdownTimerRef.current);
		}

		if (isEnabled && !isLocked) {
			const timeoutMs = inactivityTimeout * 60 * 1000; // Convert minutes to milliseconds
			const startTime = Date.now();
			
			// Set initial time remaining
			setTimeRemaining(inactivityTimeout * 60);

			// Start countdown timer (update every second)
			countdownTimerRef.current = setInterval(() => {
				const elapsed = Date.now() - startTime;
				const remaining = Math.max(0, Math.ceil((timeoutMs - elapsed) / 1000));
				setTimeRemaining(remaining);
				
				if (remaining <= 0) {
					clearInterval(countdownTimerRef.current);
					setTimeRemaining(null);
				}
			}, 1000);

			// Set main inactivity timer
			inactivityTimerRef.current = setTimeout(() => {
				setIsLocked(true);
				setTimeRemaining(null);
				if (countdownTimerRef.current) {
					clearInterval(countdownTimerRef.current);
				}
			}, timeoutMs);
		} else {
			setTimeRemaining(null);
		}
	}, [inactivityTimeout, isEnabled, isLocked]);

	// Activity listener
	const handleActivity = useCallback(() => {
		if (!isLocked) {
			resetInactivityTimer();
		}
	}, [isLocked, resetInactivityTimer]);

	// Setup activity listeners (only if user is logged in)
	useEffect(() => {
		if (!isUserLoggedIn) {
			// Clear timers if user is not logged in
			if (inactivityTimerRef.current) {
				clearTimeout(inactivityTimerRef.current);
			}
			if (countdownTimerRef.current) {
				clearInterval(countdownTimerRef.current);
			}
			setTimeRemaining(null);
			return;
		}

		if (isEnabled && !isLocked) {
			activityEvents.forEach(event => {
				document.addEventListener(event, handleActivity, true);
			});

			resetInactivityTimer();

			return () => {
				activityEvents.forEach(event => {
					document.removeEventListener(event, handleActivity, true);
				});
				if (inactivityTimerRef.current) {
					clearTimeout(inactivityTimerRef.current);
				}
				if (countdownTimerRef.current) {
					clearInterval(countdownTimerRef.current);
				}
			};
		}
	}, [isEnabled, isLocked, handleActivity, resetInactivityTimer, isUserLoggedIn]);

	// Cleanup timer on unmount
	useEffect(() => {
		return () => {
			if (inactivityTimerRef.current) {
				clearTimeout(inactivityTimerRef.current);
			}
			if (countdownTimerRef.current) {
				clearInterval(countdownTimerRef.current);
			}
		};
	}, []);

	// Lock the application immediately (only if user is logged in)
	const lockApp = () => {
		if (!isUserLoggedIn) {
			console.log('Cannot lock app: user not logged in');
			return;
		}
		if (isEnabled && (lockPattern.length > 0 || lockPasscode.length > 0)) {
			setIsLocked(true);
		}
	};

	// Unlock the application with pattern
	const unlockWithPattern = (inputPattern) => {
		if (inputPattern.length > 0 && JSON.stringify(inputPattern) === JSON.stringify(lockPattern)) {
			setIsLocked(false);
			resetInactivityTimer();
			return true;
		}
		return false;
	};

	// Unlock the application with passcode
	const unlockWithPasscode = (inputPasscode) => {
		if (inputPasscode.length > 0 && inputPasscode === lockPasscode) {
			setIsLocked(false);
			resetInactivityTimer();
			return true;
		}
		return false;
	};

	// Set new lock pattern
	const setNewLockPattern = (pattern) => {
		if (pattern && pattern.length >= 4) {
			setLockPattern(pattern);
			return true;
		}
		return false;
	};

	// Set new lock passcode
	const setNewLockPasscode = (passcode) => {
		if (passcode && passcode.length >= 4) {
			setLockPasscode(passcode);
			return true;
		}
		return false;
	};

	// Enable/disable security (only if user is logged in)
	const toggleSecurity = (enabled) => {
		if (!isUserLoggedIn) {
			console.log('Cannot toggle security: user not logged in');
			return;
		}
		setIsEnabled(enabled);
		if (!enabled) {
			setIsLocked(false);
			setTimeRemaining(null);
			if (inactivityTimerRef.current) {
				clearTimeout(inactivityTimerRef.current);
			}
			if (countdownTimerRef.current) {
				clearInterval(countdownTimerRef.current);
			}
		} else if (lockPattern.length > 0 || lockPasscode.length > 0) {
			resetInactivityTimer();
		}
	};

	// Update inactivity timeout
	const updateInactivityTimeout = (timeout) => {
		if (timeout >= 1 && timeout <= 60) {
			setInactivityTimeout(timeout);
			if (isEnabled && !isLocked) {
				resetInactivityTimer();
			}
		}
	};

	// Remove lock completely
	const removeLock = () => {
		setLockPattern([]);
		setLockPasscode('');
		setIsEnabled(false);
		setIsLocked(false);
		setTimeRemaining(null);
		localStorage.removeItem('fitT-app-locked');
		if (inactivityTimerRef.current) {
			clearTimeout(inactivityTimerRef.current);
		}
		if (countdownTimerRef.current) {
			clearInterval(countdownTimerRef.current);
		}
		return true;
	};

	// Remove lock and logout user (for use in lock screen)
	const removeLockAndLogout = () => {
		removeLock();
		// Call logout function
		applicationLogout();
		return true;
	};

	// Get remaining time before lock
	const getRemainingTime = () => {
		if (!isEnabled || isLocked) return 0;
		const elapsed = (Date.now() - lastActivity) / 1000; // seconds
		const remaining = (inactivityTimeout * 60) - elapsed;
		return Math.max(0, remaining);
	};

	const value = {
		// State
		isLocked: isUserLoggedIn ? isLocked : false,
		lockType,
		lockPattern: isUserLoggedIn ? lockPattern : [],
		lockPasscode: isUserLoggedIn ? lockPasscode : '',
		inactivityTimeout,
		isEnabled: isUserLoggedIn ? isEnabled : false,
		lastActivity,
		timeRemaining: isUserLoggedIn ? timeRemaining : null,
		isUserLoggedIn,
		
		// Actions
		lockApp,
		unlockWithPattern,
		unlockWithPasscode,
		setNewLockPattern,
		setNewLockPasscode,
		setLockType,
		toggleSecurity,
		updateInactivityTimeout,
		removeLock,
		removeLockAndLogout,
		getRemainingTime,
		
		// Computed
		hasLockSet: isUserLoggedIn && (lockPattern.length > 0 || lockPasscode.length > 0),
		isPatternType: lockType === 'pattern',
		isPasscodeType: lockType === 'passcode'
	};

	return (
		<SecurityContext.Provider value={value}>
			{children}
		</SecurityContext.Provider>
	);
};

// Custom hook to use security context
export const useSecurity = () => {
	const context = useContext(SecurityContext);
	if (!context) {
		throw new Error('useSecurity must be used within a SecurityProvider');
	}
	return context;
};